import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, ArrowRight, Save,Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerController } from '@/components/ui/date-picker';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { getPhoneValidationMessage } from '@/lib/phoneValidation';
import { toast } from 'sonner';
import DriverWizardStepper from '@/components/drivers/DriverWizardStepper';
import DocumentUploadField from '@/components/drivers/DocumentUploadField';
import {
    useCreateDriver,
    useUploadProfilePhoto,
    useUploadKycDocuments,
    useUpdateKycDocuments,
    useUploadVehicleDetails,
    useUploadBankingInfo,
    useUpdatePersonalDetails,
    useUpdateVehicleDetails,
    useUpdateVehicleDocuments,
    useUpdateBankingInfo,
    useDriver,
} from '@/api-services/hooks/useDriver';
import { useActiveVehicleCategories } from '@/api-services/hooks/useVehicleCategory';
import { adminApiClient } from '@/api-services/hooks/adminApiClient';
import { DriverDTO } from '@/api-services/hooks/adminTypes';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';
import { pricingApiClient } from '@/api-services/hooks/pricingApiClient';
import { CityMaster, StateMaster, CountryMaster } from '@/api-services/hooks/pricingTypes';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useDuplicateCheck } from '@/hooks/useDuplicateCheck';
import { useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const personalSchema = z.object({
  userName: z.string().optional().or(z.literal('')),

  password: z.string().optional().or(z.literal('')),
  confirmPassword: z.string().optional().or(z.literal('')),

  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email'),

  phoneNumber: z.string().min(1, 'Phone number is required').superRefine((val, ctx) => {
    const msg = getPhoneValidationMessage(val);
    if (msg) ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg });
  }),

  phoneCountryCode: z.string().optional().or(z.literal('')),

  dateOfBirth: z.string().optional().refine((val) => {
    if (!val) return true;

    const dob = new Date(val);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return age >= 18;
  }, {
    message: "Driver must be at least 18 years old",
  }),

  gender: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  countryId: z.string().optional().or(z.literal('')),
  stateId: z.string().optional().or(z.literal('')),
  cityId: z.string().optional().or(z.literal('')),
  emergencyContactNumber: z.string().optional().or(z.literal('')),
  emergencyPhoneCountryCode: z.string().optional().or(z.literal('')),
  preferredLanguage: z.string().optional().or(z.literal('')),
})
.refine((data) => {
  if (!data.password && !data.confirmPassword) return true;

  if (data.password.length < 6) return false;

  return data.password === data.confirmPassword;
}, {
  path: ['confirmPassword'],
  message: 'Passwords must match and be at least 6 characters',
});

const kycSchema = z.object({
    govtIdType: z.string().min(1, 'ID type is required'),
    govtIdNumber: z.string().min(1, 'ID number is required'),
});

const vehicleSchema = z.object({
    vehicleType: z.string().optional().or(z.literal('')),
    vehicleCategoryId: z.string().min(1, 'Vehicle category is required'),
    vehicleMake: z.string().min(1, 'Make is required'),
    vehicleModel: z.string().min(1, 'Model is required'),
    vehicleRegistrationNumber: z.string().min(1, 'Registration number is required'),
    yearOfManufacture: z.string().optional().or(z.literal('')),
    vehicleColor: z.string().optional().or(z.literal('')),
    seatingCapacity: z.string().optional().or(z.literal('')),
    luggageCapacity: z.string().optional().or(z.literal('')),
});

const bankingSchema = z.object({
    bankAccountHolderName: z.string().min(1, 'Account holder name is required'),
    bankName: z.string().min(1, 'Bank name is required'),
    accountNumber: z.string().min(1, 'Account number is required'),
    ifscCode: z.string().optional().or(z.literal('')),
    swiftCode: z.string().optional().or(z.literal('')),
    paymentFrequency: z.string().optional().or(z.literal('')),
});

type PersonalFormValues = z.infer<typeof personalSchema>;
type KycFormValues = z.infer<typeof kycSchema>;
type VehicleFormValues = z.infer<typeof vehicleSchema>;
type BankingFormValues = z.infer<typeof bankingSchema>;

// ---------------------------------------------------------------------------
// Steps config
// ---------------------------------------------------------------------------

const STEPS = [
    { id: 'personal', label: 'Personal Details', description: 'Basic info' },
    { id: 'photo', label: 'Profile Photo', description: 'Upload photo' },
    { id: 'kyc', label: 'KYC Documents', description: 'Identity verification' },
    { id: 'vehicle', label: 'Vehicle', description: 'Car details' },
    { id: 'bank', label: 'Banking', description: 'Payout info' },
    { id: 'review', label: 'Review', description: 'Complete' },
] as const;

const STEP_IDS = STEPS.map(s => s.id);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse location IDs from string to number for API */
const parseLocationIds = (data: PersonalFormValues) => {
    const parsed: Record<string, any> = { ...data };
    if (parsed.countryId) parsed.countryId = parseInt(parsed.countryId, 10);
    else delete parsed.countryId;
    if (parsed.stateId) parsed.stateId = parseInt(parsed.stateId, 10);
    else delete parsed.stateId;
    if (parsed.cityId) parsed.cityId = parseInt(parsed.cityId, 10);
    else delete parsed.cityId;
    return parsed;
};

/** Extract driverId from create response (handles multiple response formats) */
const extractDriverId = (response: any): number | null => {
    const id =
        response?.data?.driverId ||
        response?.driverId ||
        response?.data?.data?.driverId ||
        response?.id ||
        response?.data?.id;

    if (id) return Number(id);

    // Extract from message like "Driver (52) Saved Successfully."
    const msg = response?.message || response?.data?.message;
    if (msg) {
        const match = msg.match(/Driver\s*\((\d+)\)/);
        if (match?.[1]) return parseInt(match[1], 10);
    }

    return null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CreateDriverWizardPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    const currentStep = searchParams.get('step') || 'personal';
    const driverIdParam = searchParams.get('driverId');
    const driverId = driverIdParam ? parseInt(driverIdParam, 10) : null;
    const isEditMode = !!driverId;

    // ---- File state ----
    const [profilePhoto, setProfilePhoto] = useState<File | string | null>(null);
    const [kycFiles, setKycFiles] = useState<{
        govtIdFront: File | string | null;
        govtIdBack: File | string | null;
        licenseFront: File | string | null;
        licenseBack: File | string | null;
        selfie: File | string | null;
    }>({ govtIdFront: null, govtIdBack: null, licenseFront: null, licenseBack: null, selfie: null });
    const [vehicleFiles, setVehicleFiles] = useState<{
        ownership: File | string | null;
        insurance: File | string | null;
        roadTax: File | string | null;
    }>({ ownership: null, insurance: null, roadTax: null });

    // ---- Wizard state ----
    const [completedSteps, setCompletedSteps] = useState<string[]>([]);
    const [savedData, setSavedData] = useState<{
        personal: any; kyc: any; vehicle: any; banking: any;
    }>({ personal: null, kyc: null, vehicle: null, banking: null });

    const queryClient = useQueryClient();

    // ---- Location data ----
    const [countries, setCountries] = useState<CountryMaster[]>([]);
    const [allStates, setAllStates] = useState<StateMaster[]>([]);
    const [allCities, setAllCities] = useState<CityMaster[]>([]);

    useEffect(() => {
        pricingApiClient.get<ApiResponse<PagedData<CountryMaster>>>('/country/list', { page: 0, size: 500 })
            .then(res => setCountries(res.data?.content || [])).catch(() => {});
        pricingApiClient.get<ApiResponse<PagedData<StateMaster>>>('/state/list', { page: 0, size: 500 })
            .then(res => setAllStates(res.data?.content || [])).catch(() => {});
        pricingApiClient.get<ApiResponse<CityMaster[]>>('/city/list-only')
            .then(res => setAllCities(res.data || [])).catch(() => {});
    }, []);

    // ---- API hooks ----
    const { data: driverData, loading: loadingDriver } = useDriver(driverId || 0, { enabled: isEditMode });
    const { data: vehicleCategoriesData, isLoading: loadingCategories } = useActiveVehicleCategories();
    const vehicleCategories: any[] = Array.isArray(vehicleCategoriesData) ? vehicleCategoriesData : [];

    const { mutateAsync: createDriver, isPending: isCreating } = useCreateDriver();
    const { mutateAsync: uploadPhoto, isPending: isUploadingPhoto } = useUploadProfilePhoto();
    const { mutateAsync: uploadKyc, isPending: isUploadingKyc } = useUploadKycDocuments();
    const { mutateAsync: updateKyc, isPending: isUpdatingKyc } = useUpdateKycDocuments();
    const { mutateAsync: uploadVehicle, isPending: isUploadingVehicle } = useUploadVehicleDetails();
    const { mutateAsync: uploadBanking, isPending: isUploadingBanking } = useUploadBankingInfo();
    const { mutateAsync: updatePersonal, isPending: isUpdatingPersonal } = useUpdatePersonalDetails();
    const { mutateAsync: updateVehicleDetails, isPending: isUpdatingVehicle } = useUpdateVehicleDetails();
    const { mutateAsync: updateVehicleDocs, isPending: isUpdatingVehicleDocs } = useUpdateVehicleDocuments();
    const { mutateAsync: updateBanking, isPending: isUpdatingBanking } = useUpdateBankingInfo();

    const isLoading = isCreating || isUploadingPhoto || isUploadingKyc || isUpdatingKyc || isUploadingVehicle
        || isUploadingBanking || isUpdatingPersonal || isUpdatingVehicle || isUpdatingVehicleDocs || isUpdatingBanking;

    // ---- Forms ----
    const personalForm = useForm<PersonalFormValues>({
        resolver: zodResolver(personalSchema),
        mode: 'onChange',       
        reValidateMode: 'onChange',
        defaultValues: {
            userName: '',password: '',confirmPassword: '', firstName: '', lastName: '', email: '', phoneNumber: '', phoneCountryCode: '', dateOfBirth: '', gender: '',
            address: '', countryId: '', stateId: '', cityId: '',
            emergencyContactNumber: '', emergencyPhoneCountryCode: '', preferredLanguage: '',
        },
    });

    const kycForm = useForm<KycFormValues>({
        resolver: zodResolver(kycSchema),
        defaultValues: { govtIdType: '', govtIdNumber: '' },
    });

    const vehicleForm = useForm<VehicleFormValues>({
        resolver: zodResolver(vehicleSchema),
        defaultValues: {
            vehicleType: '', vehicleCategoryId: '', vehicleMake: '', vehicleModel: '',
            vehicleRegistrationNumber: '', yearOfManufacture: '', vehicleColor: '',
            seatingCapacity: '', luggageCapacity: '',
        },
    });

    const bankingForm = useForm<BankingFormValues>({
        resolver: zodResolver(bankingSchema),
        defaultValues: {
            bankAccountHolderName: '', bankName: '', accountNumber: '',
            ifscCode: '', swiftCode: '', paymentFrequency: '',
        },
    });

    // Duplicate email/phone check
    const { duplicateErrors, checkEmail, checkPhone } = useDuplicateCheck('driver', driverId || undefined);
    const phoneRef = React.useRef<HTMLDivElement>(null);

    // Cascading location filters
    const selectedCountryId = personalForm.watch('countryId');
    const selectedStateId = personalForm.watch('stateId');
    const filteredStates = selectedCountryId
        ? allStates.filter(s => s.country?.countryId?.toString() === selectedCountryId) : [];
    const filteredCities = selectedStateId
        ? allCities.filter(c => c.state?.stateId?.toString() === selectedStateId) : [];

    // ---- Populate forms in edit mode ----
    useEffect(() => {
        if (!driverData) return;

        const fullNumber = driverData.phoneCountryCode && driverData.phoneNumber 
            ? `${driverData.phoneCountryCode}${driverData.phoneNumber}` : driverData.phoneNumber || '';

        const fullEmergencyContactNumber = driverData.emergencyPhoneCountryCode && driverData.emergencyContactNumber 
            ? `${driverData.emergencyPhoneCountryCode}${driverData.emergencyContactNumber}` : driverData.emergencyContactNumber || '';

        personalForm.reset({
            userName: driverData.userName || driverData.userName?.split(' ')[0] || '',
            firstName: driverData.firstName || driverData.fullName?.split(' ')[0] || '',
            lastName: driverData.lastName || driverData.fullName?.split(' ').slice(1).join(' ') || '',
            email: driverData.email || '',
            phoneNumber: fullNumber, // ✅ E.164 format
            phoneCountryCode: driverData.phoneCountryCode || '',
            dateOfBirth: driverData.dateOfBirth || '',
            gender: driverData.gender || '',
            address: driverData.address || '',
            countryId: driverData.countryId?.toString() || '',
            stateId: driverData.stateId?.toString() || '',
            cityId: driverData.cityId?.toString() || '',
            emergencyContactNumber: fullEmergencyContactNumber, // ✅ E.164 format
            emergencyPhoneCountryCode: driverData.emergencyPhoneCountryCode || '',
            preferredLanguage: driverData.preferredLanguage || '',
        });

        if (driverData.profilePhotoUrl) setProfilePhoto(driverData.profilePhotoUrl);

        kycForm.reset({
            govtIdType: driverData.govtIdType || '',
            govtIdNumber: driverData.govtIdNumber || '',
        });
        setKycFiles({
            govtIdFront: driverData.govtIdFrontUrl || null,
            govtIdBack: driverData.govtIdBackUrl || null,
            licenseFront: driverData.drivingLicenseFrontUrl || null,
            licenseBack: driverData.drivingLicenseBackUrl || null,
            selfie: driverData.selfieWithIdUrl || null,
        });

        vehicleForm.reset({
            vehicleType: driverData.vehicleType || '',
            vehicleCategoryId: driverData.vehicleCategoryId?.toString() || '',
            vehicleMake: driverData.vehicleMake || '',
            vehicleModel: driverData.vehicleModel || '',
            vehicleRegistrationNumber: driverData.vehicleRegistrationNumber || '',
            yearOfManufacture: driverData.yearOfManufacture?.toString() || '',
            vehicleColor: driverData.vehicleColor || '',
            seatingCapacity: driverData.seatingCapacity?.toString() || '',
            luggageCapacity: driverData.luggageCapacity?.toString() || '',
        });
        setVehicleFiles({
            ownership: driverData.ownershipDocUrl || null,
            insurance: driverData.insuranceDocUrl || driverData.vehicleInsuranceCertUrl || null,
            roadTax: driverData.roadTaxDocUrl || null,
        });

        bankingForm.reset({
            bankAccountHolderName: driverData.bankAccountHolderName || '',
            bankName: driverData.bankName || '',
            accountNumber: driverData.accountNumber || driverData.bankAccountNumber || '',
            ifscCode: driverData.ifscCode || driverData.bankIfscCode || '',
            swiftCode: driverData.swiftCode || '',
            paymentFrequency: driverData.paymentFrequency || '',
        });

        setCompletedSteps(['personal', 'photo', 'kyc', 'vehicle', 'bank', 'review']);
    }, [driverData]);

    // ---- Navigation helpers ----
    const markComplete = useCallback((stepId: string) => {
        setCompletedSteps(prev => prev.includes(stepId) ? prev : [...prev, stepId]);
    }, []);

    const goToStep = useCallback((stepId: string) => {
        const params: Record<string, string> = { step: stepId };
        if (driverId) params.driverId = driverId.toString();
        setSearchParams(params);
        window.scrollTo(0, 0);
    }, [driverId, setSearchParams]);

    const handleStepClick = (stepId: string) => {
        if (isEditMode) goToStep(stepId);
    };

    // ---- Step 1: Personal ----
    const savePersonal = async (andAdvance: boolean) => {
        const valid = await personalForm.trigger();
          const hasDuplicate =
    duplicateErrors.email ||
    duplicateErrors.phone;

  if (!valid || hasDuplicate) {
    if (duplicateErrors.phone) {
      toast.error(duplicateErrors.phone);
      phoneRef.current?.querySelector('input')?.focus();
    } else if (duplicateErrors.email) {
      toast.error(duplicateErrors.email);
      personalForm.setFocus('email');
    }
    return;
  }
        const data = personalForm.getValues();
        const parsed: any = parseLocationIds(data);
        parsed.fullName = `${(parsed.firstName || '').trim()} ${(parsed.lastName || '').trim()}`.trim();

        let phoneCountryCode = '';
        let mobileNumber = '';
        if(parsed.phoneNumber){
            const phone = parsePhoneNumberFromString(parsed.phoneNumber);    
            if (phone) {
                phoneCountryCode = `+${phone.countryCallingCode}`; // +91
                mobileNumber = phone.nationalNumber;              // 9876543210
            }
            parsed.phoneCountryCode = phoneCountryCode;
            parsed.phoneNumber = mobileNumber;
        }

        let emergencyContactNumber = '';
        let emergencyPhoneCountryCode = '';
        if(parsed.emergencyContactNumber){
            const phone = parsePhoneNumberFromString(parsed.emergencyContactNumber);
            if (phone) {
                emergencyPhoneCountryCode = `+${phone.countryCallingCode}`; // +91
                emergencyContactNumber = phone.nationalNumber;              // 9876543210
            }
            parsed.emergencyContactNumber = emergencyContactNumber;
            parsed.emergencyPhoneCountryCode = emergencyPhoneCountryCode;
        }

        console.log("personal details saving method ==========",parsed)
        try {
            if (isEditMode && driverId) {
                await updatePersonal({ id: driverId, data: parsed });
                toast.success('Personal details saved');
            } else {
                setSavedData(prev => ({ ...prev, personal: parsed }));
                if (andAdvance) toast.success('Personal details saved');
            }
            markComplete('personal');
            if (andAdvance) goToStep('photo');
        } catch (err: any) {
            toast.error(`Failed: ${err.message}`);
        }
    };

    // ---- Step 2: Photo ----
    const savePhoto = async () => {
        if (isEditMode && driverId && profilePhoto instanceof File) {
            try {
                await uploadPhoto({ driverId, file: profilePhoto });
                toast.success('Photo uploaded');
            } catch (err: any) {
                toast.error(`Upload failed: ${err.message}`);
                return;
            }
        }
        markComplete('photo');
        goToStep('kyc');
    };

    // ---- Step 3: KYC ----
    const [kycFileErrors, setKycFileErrors] = useState<Record<string, string>>({});

    const saveKyc = async (andAdvance: boolean) => {
        const valid = await kycForm.trigger();

        // Validate required document files
        const fileErrors: Record<string, string> = {};
        if (!kycFiles.govtIdFront) fileErrors.govtIdFront = 'Government ID Front is required';
        if (!kycFiles.govtIdBack) fileErrors.govtIdBack = 'Government ID Back is required';
        if (!kycFiles.licenseFront) fileErrors.licenseFront = 'Driving License Front is required';
        if (!kycFiles.licenseBack) fileErrors.licenseBack = 'Driving License Back is required';
        if (!kycFiles.selfie) fileErrors.selfie = 'Selfie with ID is required';
        setKycFileErrors(fileErrors);

        if (!valid || Object.keys(fileErrors).length > 0) {
            if (Object.keys(fileErrors).length > 0) {
                toast.error('Please upload all required KYC documents');
            }
            return;
        }

        const data = kycForm.getValues();

        const filesToUpload: Record<string, File> = {};
        if (kycFiles.govtIdFront instanceof File) filesToUpload.govtIdFrontUrl = kycFiles.govtIdFront;
        if (kycFiles.govtIdBack instanceof File) filesToUpload.govtIdBackUrl = kycFiles.govtIdBack;
        if (kycFiles.licenseFront instanceof File) filesToUpload.drivingLicenseFrontUrl = kycFiles.licenseFront;
        if (kycFiles.licenseBack instanceof File) filesToUpload.drivingLicenseBackUrl = kycFiles.licenseBack;
        if (kycFiles.selfie instanceof File) filesToUpload.selfieWithIdUrl = kycFiles.selfie;

        try {
            if (isEditMode && driverId) {
                if (Object.keys(filesToUpload).length > 0) {
                    await updateKyc({ driverId, govtIdType: data.govtIdType, govtIdNumber: data.govtIdNumber, files: filesToUpload });
                }
                toast.success('KYC details saved');
            } else {
                setSavedData(prev => ({ ...prev, kyc: data }));
                if (andAdvance) toast.success('KYC details saved');
            }
            markComplete('kyc');
            if (andAdvance) goToStep('vehicle');
        } catch (err: any) {
            toast.error(err.message || 'Failed to save KYC details');
        }
    };

    // ---- Step 4: Vehicle ----
    const [vehicleFileErrors, setVehicleFileErrors] = useState<Record<string, string>>({});

    const saveVehicle = async (andAdvance: boolean) => {
        const valid = await vehicleForm.trigger();

        // Validate required vehicle documents
        const fileErrors: Record<string, string> = {};
        if (!vehicleFiles.ownership) fileErrors.ownership = 'Ownership Document is required';
        if (!vehicleFiles.roadTax) fileErrors.roadTax = 'Road Tax Document is required';
        setVehicleFileErrors(fileErrors);

        if (!valid || Object.keys(fileErrors).length > 0) {
            if (Object.keys(fileErrors).length > 0) {
                toast.error('Please upload all required vehicle documents');
            }
            return;
        }

        const data = vehicleForm.getValues();

        const vehiclePayload = {
            vehicleType: data.vehicleType || '',
            vehicleCategoryId: data.vehicleCategoryId ? parseInt(data.vehicleCategoryId) : 0,
            vehicleMake: data.vehicleMake,
            vehicleModel: data.vehicleModel,
            vehicleRegistrationNumber: data.vehicleRegistrationNumber,
            yearOfManufacture: data.yearOfManufacture ? parseInt(data.yearOfManufacture) : 0,
            vehicleColor: data.vehicleColor || '',
            seatingCapacity: data.seatingCapacity ? parseInt(data.seatingCapacity) : 4,
            luggageCapacity: data.luggageCapacity ? parseInt(data.luggageCapacity) : 0,
        };

        const docFiles: Record<string, File> = {};
        if (vehicleFiles.ownership instanceof File) docFiles.ownershipDocUrl = vehicleFiles.ownership;
        if (vehicleFiles.insurance instanceof File) docFiles.insuranceDocUrl = vehicleFiles.insurance;
        if (vehicleFiles.roadTax instanceof File) docFiles.roadTaxDocUrl = vehicleFiles.roadTax;

        try {
            if (isEditMode && driverId) {
                await updateVehicleDetails({ driverId, ...vehiclePayload });
                if (Object.keys(docFiles).length > 0) {
                    await updateVehicleDocs({ driverId, files: docFiles });
                }
                toast.success('Vehicle details saved');
            } else {
                setSavedData(prev => ({ ...prev, vehicle: data }));
                if (andAdvance) toast.success('Vehicle details saved');
            }
            markComplete('vehicle');
            if (andAdvance) goToStep('bank');
        } catch (err: any) {
            toast.error(`Failed: ${err.message}`);
        }
    };

    // ---- Step 5: Banking ----
    const saveBanking = async (andAdvance: boolean) => {
        const valid = await bankingForm.trigger();
        if (!valid) return;
        const data = bankingForm.getValues();

        try {
            if (isEditMode && driverId) {
                await updateBanking({ driverId, ...data, eWalletId: '' });
                toast.success('Banking details saved');
            } else {
                setSavedData(prev => ({ ...prev, banking: data }));
                if (andAdvance) toast.success('Banking details saved');
            }
            markComplete('bank');
            if (andAdvance) goToStep('review');
        } catch (err: any) {
            toast.error(`Failed: ${err.message}`);
        }
    };

    // ---- Step 6: Complete ----
    const onComplete = async () => {
        if (isEditMode) {
            toast.success('Driver details updated!');
            navigate('/admin/drivers');
            return;
        }

        // CREATE MODE: chain all API calls
        try {
            // 1. Create driver
            const createResponse: any = await createDriver({
                ...savedData.personal,
                email: savedData.personal?.email || '',
                createdDate: new Date().toISOString(),
            });

            let newDriverId = extractDriverId(createResponse);

            // Fallback: look up by phone
            if (!newDriverId && savedData.personal?.phoneNumber) {
                await new Promise(r => setTimeout(r, 1000));
                try {
                    const listRes = await adminApiClient.get<any>('/drivers/list', { page: 0, size: 10 });
                    const list = listRes.data?.content || listRes.content || (Array.isArray(listRes.data) ? listRes.data : []);
                    const found = list
                        .filter((d: DriverDTO) => d.phoneNumber === savedData.personal.phoneNumber)
                        .sort((a: DriverDTO, b: DriverDTO) => (b.driverId || 0) - (a.driverId || 0))[0];
                    if (found?.driverId) newDriverId = found.driverId;
                } catch { /* ignore */ }
            }

            if (!newDriverId) {
                toast.error('Driver created but could not retrieve ID. Check the drivers list.');
                navigate('/admin/drivers');
                return;
            }

            toast.success(`Driver created (ID: ${newDriverId})`);

            // 2. Photo
            if (profilePhoto instanceof File) {
                await uploadPhoto({ driverId: newDriverId, file: profilePhoto });
            }

            // 3. KYC
            if (savedData.kyc) {
                const kycFilesToUpload: Record<string, File> = {};
                if (kycFiles.govtIdFront instanceof File) kycFilesToUpload.govtIdFrontUrl = kycFiles.govtIdFront;
                if (kycFiles.govtIdBack instanceof File) kycFilesToUpload.govtIdBackUrl = kycFiles.govtIdBack;
                if (kycFiles.licenseFront instanceof File) kycFilesToUpload.drivingLicenseFrontUrl = kycFiles.licenseFront;
                if (kycFiles.licenseBack instanceof File) kycFilesToUpload.drivingLicenseBackUrl = kycFiles.licenseBack;
                if (kycFiles.selfie instanceof File) kycFilesToUpload.selfieWithIdUrl = kycFiles.selfie;

                if (Object.keys(kycFilesToUpload).length > 0) {
                    await uploadKyc({
                        driverId: newDriverId,
                        govtIdType: savedData.kyc.govtIdType,
                        govtIdNumber: savedData.kyc.govtIdNumber,
                        files: kycFilesToUpload,
                    });
                }
            }

            // 4. Vehicle
            if (savedData.vehicle) {
                const vFiles: Record<string, File> = {};
                if (vehicleFiles.ownership instanceof File) vFiles.ownershipDocUrl = vehicleFiles.ownership;
                if (vehicleFiles.insurance instanceof File) vFiles.insuranceDocUrl = vehicleFiles.insurance;
                if (vehicleFiles.roadTax instanceof File) vFiles.roadTaxDocUrl = vehicleFiles.roadTax;

                await uploadVehicle({
                    driverId: newDriverId,
                    vehicleType: savedData.vehicle.vehicleType || '',
                    vehicleCategoryId: savedData.vehicle.vehicleCategoryId ? parseInt(savedData.vehicle.vehicleCategoryId) : 0,
                    vehicleMake: savedData.vehicle.vehicleMake,
                    vehicleModel: savedData.vehicle.vehicleModel,
                    vehicleRegistrationNumber: savedData.vehicle.vehicleRegistrationNumber,
                    yearOfManufacture: savedData.vehicle.yearOfManufacture ? parseInt(savedData.vehicle.yearOfManufacture) : 0,
                    vehicleColor: savedData.vehicle.vehicleColor || '',
                    seatingCapacity: savedData.vehicle.seatingCapacity ? parseInt(savedData.vehicle.seatingCapacity) : 4,
                    luggageCapacity: savedData.vehicle.luggageCapacity ? parseInt(savedData.vehicle.luggageCapacity) : 0,
                    files: vFiles,
                });
            }

            // 5. Banking
            if (savedData.banking) {
                await uploadBanking({
                    driverId: newDriverId,
                    ...savedData.banking,
                    eWalletId: '',
                });
            }

            toast.success('Driver onboarding completed!');
            await queryClient.invalidateQueries({ queryKey: ['drivers'] });
            navigate('/admin/drivers');
            return;
        } catch (err: any) {
            toast.error(`Onboarding failed: ${err.message}`);
        }
    };

    // ---- Back button ----
    const handleBack = () => {
        const idx = STEP_IDS.indexOf(currentStep);
        if (idx > 0) goToStep(STEP_IDS[idx - 1]);
        else navigate('/admin/drivers');
    };

    // ---- Loading state ----
    if (isEditMode && loadingDriver) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // ====================================================================
    // RENDER
    // ====================================================================

    return (
        <div className="space-y-6 animate-fade-in max-w-5xl mx-auto pb-10">
            {/* Header */}
            <div className="flex items-center space-x-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/drivers')} className="hover:bg-muted">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Drivers
                </Button>
                <div>
                    <h1 className="text-3xl font-bold text-foreground">
                        {isEditMode ? 'Edit Driver' : 'Add New Driver'}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {isEditMode ? 'Update driver details' : 'Complete the wizard to register a new driver'}
                    </p>
                </div>
            </div>

            {/* Stepper */}
            <Card>
                <CardContent className="pt-6">
                    <DriverWizardStepper
                        steps={[...STEPS]}
                        currentStep={currentStep}
                        completedSteps={completedSteps}
                        onStepClick={handleStepClick}
                        allowNavigation={isEditMode}
                    />
                </CardContent>
            </Card>

            {/* Step Content */}
            <Card className="min-h-[400px]">
                <CardHeader>
                    <CardTitle>{STEPS.find(s => s.id === currentStep)?.label}</CardTitle>
                    <CardDescription>{STEPS.find(s => s.id === currentStep)?.description}</CardDescription>
                </CardHeader>

                <CardContent>
                    {/* ============= PERSONAL ============= */}
                    {currentStep === 'personal' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>User Name</Label>
                                    <Input {...personalForm.register('userName')} placeholder="Autofilled from phone number" readOnly/>
                                </div>

{!isEditMode && (
  <>
    {/* Password */}
    <div className="space-y-2">
      <Label htmlFor="password">Password *</Label>

      <div className="relative">
        <Input
          id="password"
          type={showPassword ? 'text' : 'password'}
          {...personalForm.register('password')}
          className="pr-10"
          placeholder='Enter Password'
        />

        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {personalForm.formState.errors.password && (
        <p className="text-sm text-destructive">
          {personalForm.formState.errors.password.message}
        </p>
      )}
    </div>

    {/* Confirm Password */}
    <div className="space-y-2">
      <Label htmlFor="confirmPassword">Confirm Password *</Label>

      <div className="relative">
        <Input
          id="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          {...personalForm.register('confirmPassword')}
          className="pr-10"
            placeholder='Confirm Password'
        />

        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
        >
          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {personalForm.formState.errors.confirmPassword && (
        <p className="text-sm text-destructive">
          {personalForm.formState.errors.confirmPassword.message}
        </p>
      )}
    </div>
  </>
)}

                                 <div className="space-y-2">
                                    <Label>First Name *</Label>
                                    <Input {...personalForm.register('firstName')} placeholder="John" />
                                    {personalForm.formState.errors.firstName && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.firstName.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Last Name *</Label>
                                    <Input {...personalForm.register('lastName')} placeholder="Doe" />
                                    {personalForm.formState.errors.lastName && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.lastName.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Phone Number *</Label>
                                    <Controller
                                        control={personalForm.control}
                                        name="phoneNumber"
                                        render={({ field }) => (
                                            <PhoneInput
                                                ref={phoneRef}
                                                value={field.value}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    const phone = parsePhoneNumberFromString(value);
                                                    if (phone) {
                                                        if(!driverData){
                                                            personalForm.setValue("userName", phone.nationalNumber || "");
                                                        }
                                                    }
                                                }}
                                                onBlur={async () => {
  const rawInput = field.value;

  const parsedInput = parsePhoneNumberFromString(rawInput);
  const parsedExisting = parsePhoneNumberFromString(driverData?.phoneNumber || '');

  const inputNational = parsedInput?.nationalNumber || '';
  const existingNational = parsedExisting?.nationalNumber || driverData?.phoneNumber || '';

  if (isEditMode && inputNational === existingNational) return;

  const dup = await checkPhone(inputNational);

  if (dup) {
    personalForm.setError('phoneNumber', {
      type: 'manual',
    //   message: 'Phone number already exists',
    });
  } else {
    personalForm.clearErrors('phoneNumber');
  }
}}
                                                hasError={!!(personalForm.formState.errors.phoneNumber || duplicateErrors.phone)}
                                            />
                                        )}
                                    />
                                    {(personalForm.formState.errors.phoneNumber || duplicateErrors.phone) && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.phoneNumber?.message || duplicateErrors.phone}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Email *</Label>
                                    <Input {...personalForm.register('email', {
                                        onBlur: async () => {
                                             const email = personalForm.getValues('email');

  if (isEditMode && driverData.email === email) return;

  const dup = await checkEmail(email);

  if (dup) {
    personalForm.setError('email', {
      type: 'manual',
    //   message: 'Email already exists',
    });
    personalForm.setFocus('email');
  } else {
    personalForm.clearErrors('email');
  }
                                        },
                                    })} placeholder="john@example.com" />
                                    {(personalForm.formState.errors.email || duplicateErrors.email) && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.email?.message || duplicateErrors.email}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <DatePickerController
                                        control={personalForm.control}
                                        name="dateOfBirth"
                                        label="Date of Birth"
                                        placeholder="Select date of birth"
                                        maxDate={new Date()}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Gender</Label>
                                    <Controller control={personalForm.control} name="gender"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="MALE">Male</SelectItem>
                                                    <SelectItem value="FEMALE">Female</SelectItem>
                                                    {/* <SelectItem value="OTHER">Other</SelectItem> */}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Preferred Language</Label>
                                    <Controller control={personalForm.control} name="preferredLanguage"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger><SelectValue placeholder="Select language" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="English">English</SelectItem>
                                                    <SelectItem value="Spanish">Spanish</SelectItem>
                                                    <SelectItem value="French">French</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label>Address</Label>
                                    <Input {...personalForm.register('address')} placeholder="123 Main St" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Emergency Contact</Label>
                                    <Controller
                                        control={personalForm.control}
                                        name="emergencyContactNumber"
                                        render={({ field }) => (
                                            <PhoneInput
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Country</Label>
                                    <Controller control={personalForm.control} name="countryId"
                                        render={({ field }) => (
                                            <Select onValueChange={(val) => {
                                                field.onChange(val);
                                                personalForm.setValue('stateId', '');
                                                personalForm.setValue('cityId', '');
                                            }} value={field.value} disabled={countries.length === 0}>
                                                <SelectTrigger><SelectValue placeholder={countries.length === 0 ? "No countries available" : "Select country"} /></SelectTrigger>
                                                <SelectContent>
                                                    {countries.map(c => (
                                                        <SelectItem key={c.countryId} value={c.countryId!.toString()}>
                                                            {c.countryName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>State</Label>
                                    <Controller control={personalForm.control} name="stateId"
                                        render={({ field }) => (
                                            <Select onValueChange={(val) => {
                                                field.onChange(val);
                                                personalForm.setValue('cityId', '');
                                            }} value={field.value} disabled={!selectedCountryId || filteredStates.length === 0}>
                                                <SelectTrigger><SelectValue placeholder={!selectedCountryId ? "Select a country first" : filteredStates.length === 0 ? "No states available" : "Select state"} /></SelectTrigger>
                                                <SelectContent>
                                                    {filteredStates.map(s => (
                                                        <SelectItem key={s.stateId} value={s.stateId!.toString()}>
                                                            {s.stateName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>City</Label>
                                    <Controller control={personalForm.control} name="cityId"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value} disabled={!selectedStateId || filteredCities.length === 0}>
                                                <SelectTrigger><SelectValue placeholder={!selectedStateId ? "Select a state first" : filteredCities.length === 0 ? "No cities available" : "Select city"} /></SelectTrigger>
                                                <SelectContent>
                                                    {filteredCities.map(c => (
                                                        <SelectItem key={c.cityId} value={c.cityId!.toString()}>
                                                            {c.cityName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ============= PHOTO ============= */}
                    {currentStep === 'photo' && (
                        <div className="space-y-6 max-w-md mx-auto text-center">
                            <DocumentUploadField
                                label="Profile Photo"
                                value={profilePhoto || undefined}
                                onChange={setProfilePhoto}
                                accept="image/*"
                                placeholder="Upload a clear face photo"
                            />
                        </div>
                    )}

                    {/* ============= KYC ============= */}
                    {currentStep === 'kyc' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Government ID Type *</Label>
                                    <Controller control={kycForm.control} name="govtIdType"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger><SelectValue placeholder="Select ID type" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="NATIONAL_ID">National ID</SelectItem>
                                                    <SelectItem value="PASSPORT">Passport</SelectItem>
                                                    <SelectItem value="DRIVING_LICENSE">Driving License</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {kycForm.formState.errors.govtIdType && (
                                        <p className="text-sm text-destructive">{kycForm.formState.errors.govtIdType.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>ID Number *</Label>
                                    <Input {...kycForm.register('govtIdNumber')} placeholder="Enter ID number" />
                                    {kycForm.formState.errors.govtIdNumber && (
                                        <p className="text-sm text-destructive">{kycForm.formState.errors.govtIdNumber.message}</p>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <DocumentUploadField label="Government ID Front" value={kycFiles.govtIdFront || undefined}
                                        onChange={f => { setKycFiles(p => ({ ...p, govtIdFront: f })); setKycFileErrors(p => ({ ...p, govtIdFront: '' })); }} required />
                                    {kycFileErrors.govtIdFront && <p className="text-sm text-destructive mt-1">{kycFileErrors.govtIdFront}</p>}
                                </div>
                                <div>
                                    <DocumentUploadField label="Government ID Back" value={kycFiles.govtIdBack || undefined}
                                        onChange={f => { setKycFiles(p => ({ ...p, govtIdBack: f })); setKycFileErrors(p => ({ ...p, govtIdBack: '' })); }} required />
                                    {kycFileErrors.govtIdBack && <p className="text-sm text-destructive mt-1">{kycFileErrors.govtIdBack}</p>}
                                </div>
                                <div>
                                    <DocumentUploadField label="Driving License Front" value={kycFiles.licenseFront || undefined}
                                        onChange={f => { setKycFiles(p => ({ ...p, licenseFront: f })); setKycFileErrors(p => ({ ...p, licenseFront: '' })); }} required />
                                    {kycFileErrors.licenseFront && <p className="text-sm text-destructive mt-1">{kycFileErrors.licenseFront}</p>}
                                </div>
                                <div>
                                    <DocumentUploadField label="Driving License Back" value={kycFiles.licenseBack || undefined}
                                        onChange={f => { setKycFiles(p => ({ ...p, licenseBack: f })); setKycFileErrors(p => ({ ...p, licenseBack: '' })); }} required />
                                    {kycFileErrors.licenseBack && <p className="text-sm text-destructive mt-1">{kycFileErrors.licenseBack}</p>}
                                </div>
                                <div>
                                    <DocumentUploadField label="Selfie with ID" value={kycFiles.selfie || undefined}
                                        onChange={f => { setKycFiles(p => ({ ...p, selfie: f })); setKycFileErrors(p => ({ ...p, selfie: '' })); }} required />
                                    {kycFileErrors.selfie && <p className="text-sm text-destructive mt-1">{kycFileErrors.selfie}</p>}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ============= VEHICLE ============= */}
                    {currentStep === 'vehicle' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Vehicle Category *</Label>
                                    <Controller control={vehicleForm.control} name="vehicleCategoryId"
                                        render={({ field }) => (
                                            <Select disabled={loadingCategories}
                                                onValueChange={val => {
                                                    field.onChange(val);
                                                    const cat = vehicleCategories.find((c: any) => c.vehicleCategoryId.toString() === val);
                                                    if (cat) {
                                                        vehicleForm.setValue('vehicleType', cat.vehicleCategoryName);
                                                        vehicleForm.setValue('seatingCapacity', cat.maxPassengerCount?.toString() || '');
                                                        vehicleForm.setValue('luggageCapacity', cat.maxLuggageVolume?.toString() || '');
                                                    }
                                                }}
                                                value={field.value}>
                                                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                                                <SelectContent>
                                                    {vehicleCategories.map((c: any) => (
                                                        <SelectItem key={c.vehicleCategoryId} value={c.vehicleCategoryId.toString()}>
                                                            {c.vehicleCategoryName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {vehicleForm.formState.errors.vehicleCategoryId && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleCategoryId.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Make *</Label>
                                    <Input {...vehicleForm.register('vehicleMake')} placeholder="Toyota" />
                                    {vehicleForm.formState.errors.vehicleMake && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleMake.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Model *</Label>
                                    <Input {...vehicleForm.register('vehicleModel')} placeholder="Corolla" />
                                    {vehicleForm.formState.errors.vehicleModel && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleModel.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Registration Number *</Label>
                                    <Input {...vehicleForm.register('vehicleRegistrationNumber')} placeholder="ABC-1234" />
                                    {vehicleForm.formState.errors.vehicleRegistrationNumber && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleRegistrationNumber.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Year</Label>
                                    <Input type="number" {...vehicleForm.register('yearOfManufacture')} placeholder="2020" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Color</Label>
                                    <Input {...vehicleForm.register('vehicleColor')} placeholder="White" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Seating Capacity</Label>
                                    <Input type="number" {...vehicleForm.register('seatingCapacity')} placeholder="Auto-filled from vehicle category" readOnly className="bg-muted" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Luggage Capacity</Label>
                                    <Input type="number" {...vehicleForm.register('luggageCapacity')} placeholder="Auto-filled from vehicle category" readOnly className="bg-muted" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <DocumentUploadField label="Ownership Document" value={vehicleFiles.ownership || undefined}
                                        onChange={f => { setVehicleFiles(p => ({ ...p, ownership: f })); setVehicleFileErrors(p => ({ ...p, ownership: '' })); }} required />
                                    {vehicleFileErrors.ownership && <p className="text-sm text-destructive mt-1">{vehicleFileErrors.ownership}</p>}
                                </div>
                                <DocumentUploadField label="Insurance Document" value={vehicleFiles.insurance || undefined}
                                    onChange={f => setVehicleFiles(p => ({ ...p, insurance: f }))} />
                                <div>
                                    <DocumentUploadField label="Road Tax Document" value={vehicleFiles.roadTax || undefined}
                                        onChange={f => { setVehicleFiles(p => ({ ...p, roadTax: f })); setVehicleFileErrors(p => ({ ...p, roadTax: '' })); }} required />
                                    {vehicleFileErrors.roadTax && <p className="text-sm text-destructive mt-1">{vehicleFileErrors.roadTax}</p>}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ============= BANKING ============= */}
                    {currentStep === 'bank' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Account Holder Name *</Label>
                                    <Input {...bankingForm.register('bankAccountHolderName')} placeholder="John Doe" />
                                    {bankingForm.formState.errors.bankAccountHolderName && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.bankAccountHolderName.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Bank Name *</Label>
                                    <Input {...bankingForm.register('bankName')} placeholder="Enter Bank Name" />
                                    {bankingForm.formState.errors.bankName && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.bankName.message}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Account Number *</Label>
                                    <Input {...bankingForm.register('accountNumber')} placeholder="1234567890" />
                                    {bankingForm.formState.errors.accountNumber && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.accountNumber.message}</p>
                                    )}
                                </div>
                                {/* <div className="space-y-2">
                                    <Label>IFSC / Routing Code *</Label>
                                    <Input {...bankingForm.register('ifscCode')} placeholder="ABCD0123456" />
                                    {bankingForm.formState.errors.ifscCode && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.ifscCode.message}</p>
                                    )}
                                </div> */}
                                <div className="space-y-2">
                                    <Label>SWIFT Code</Label>
                                    <Input {...bankingForm.register('swiftCode')} placeholder="ABCDUS33" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Payment Frequency</Label>
                                    <Controller control={bankingForm.control} name="paymentFrequency"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger><SelectValue placeholder="Select frequency" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="WEEKLY">Weekly</SelectItem>
                                                    <SelectItem value="BI-WEEKLY">Bi-Weekly</SelectItem>
                                                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ============= REVIEW ============= */}
                    {currentStep === 'review' && (
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-success" />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">Ready to Complete</h2>
                            <p className="text-muted-foreground mb-8">
                                All information has been entered. Click below to complete.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                                <Card>
                                    <CardHeader className="pb-2"><CardTitle className="text-base">Personal</CardTitle></CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground">Name: {personalForm.getValues('firstName')} {personalForm.getValues('lastName')}</p>
                                        <p className="text-sm text-muted-foreground">Phone: {personalForm.getValues('phoneNumber')}</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2"><CardTitle className="text-base">Vehicle</CardTitle></CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground">Type: {vehicleForm.getValues('vehicleType')}</p>
                                        <p className="text-sm text-muted-foreground">Reg: {vehicleForm.getValues('vehicleRegistrationNumber')}</p>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}
                </CardContent>

                {/* Footer buttons */}
                <CardFooter className="flex justify-between border-t pt-6">
                    <Button variant="outline" onClick={handleBack} disabled={isLoading}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>

                    <div className="flex gap-2">
                        {currentStep === 'personal' && (
                            <>
                                {isEditMode && (
                                    <Button variant="outline" disabled={isLoading} onClick={() => savePersonal(false)}>
                                        {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" /> Save
                                    </Button>
                                )}
                                <Button disabled={isLoading} onClick={() => savePersonal(true)}>
                                    {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Next <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </>
                        )}

                        {currentStep === 'photo' && (
                            <Button onClick={savePhoto} disabled={isLoading}>
                                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {profilePhoto ? 'Upload & Next' : 'Skip & Next'}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        )}

                        {currentStep === 'kyc' && (
                            <>
                                {isEditMode && (
                                    <Button variant="outline" disabled={isLoading} onClick={() => saveKyc(false)}>
                                        {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" /> Save
                                    </Button>
                                )}
                                <Button disabled={isLoading} onClick={() => saveKyc(true)}>
                                    {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Next <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </>
                        )}

                        {currentStep === 'vehicle' && (
                            <>
                                {isEditMode && (
                                    <Button variant="outline" disabled={isLoading} onClick={() => saveVehicle(false)}>
                                        {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" /> Save
                                    </Button>
                                )}
                                <Button disabled={isLoading} onClick={() => saveVehicle(true)}>
                                    {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Next <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </>
                        )}

                        {currentStep === 'bank' && (
                            <>
                                {isEditMode && (
                                    <Button variant="outline" disabled={isLoading} onClick={() => saveBanking(false)}>
                                        {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" /> Save
                                    </Button>
                                )}
                                <Button disabled={isLoading} onClick={() => saveBanking(true)}>
                                    {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Next <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </>
                        )}

                        {currentStep === 'review' && (
                            <Button onClick={onComplete} disabled={isLoading} className="bg-success hover:bg-success/90 text-success-foreground">
                                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Complete {isEditMode ? 'Update' : 'Onboarding'}
                            </Button>
                        )}
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
};

export default CreateDriverWizardPage;
